import { formatDistanceToNowStrict } from 'date-fns';
import { he } from 'date-fns/locale/he';
import Calendar from 'lucide-react/dist/esm/icons/calendar';
import Clock from 'lucide-react/dist/esm/icons/clock';
import Coins from 'lucide-react/dist/esm/icons/coins';
import Target from 'lucide-react/dist/esm/icons/target';
import TrendingDown from 'lucide-react/dist/esm/icons/trending-down';
import TrendingUp from 'lucide-react/dist/esm/icons/trending-up';
import { formatHebrew } from '@/lib/format-hebrew';

interface Price {
  price: number | null;
  date: string | Date;
}

interface StatsProps {
  current: Price;
  startDate: string | Date;
  max: Price;
  min: Price;
  meanPrice: number;
  prices: Price[];
}

export default function Stats({ current, startDate, max, min, meanPrice, prices }: StatsProps) {
  const distance = (date: string | Date) =>
    formatDistanceToNowStrict(new Date(date), { locale: he });

  const inStockOnlyPrices = prices
    .filter((x) => x.price)
    .filter((num, i, arr) => num.price !== arr[i - 1]?.price) as Array<Required<Price>>;

  const recent = inStockOnlyPrices.at(-1)!;

  const hadMoreThanSinglePrice = inStockOnlyPrices.length > 1;
  const previous = hadMoreThanSinglePrice ? inStockOnlyPrices.at(-2)! : null;
  const diff = hadMoreThanSinglePrice && previous ? recent.price! - previous.price! : 0;
  const percent =
    hadMoreThanSinglePrice && previous
      ? Number((((recent.price! - previous.price!) / previous.price!) * 100).toFixed(2))
      : 0;
  const signChar = hadMoreThanSinglePrice ? (diff > 0 ? '+' : '-') : '-';

  return (
    <div className="stats-container space-y-4">
      <div className="current-price-card rounded-xl border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 p-4 transition-all duration-300 hover:shadow-md hover:shadow-blue-500/10 dark:border-blue-800 dark:from-blue-950/20 dark:to-indigo-950/20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-blue-100 p-2 dark:bg-blue-900/50">
              <Coins className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="text-sm font-medium text-blue-700 dark:text-blue-300">
              {current.price && current.price > 0 ? 'מחיר נוכחי' : 'מחיר אחרון'}
            </div>
          </div>
          <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">{recent.price}₪</div>
        </div>
      </div>
      {inStockOnlyPrices.length > 1 && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="stat-card rounded-xl border border-gray-200 bg-gray-50 p-2 transition-all duration-300 hover:-translate-y-px hover:bg-gray-100 hover:shadow-md hover:shadow-gray-500/10 md:p-3 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700">
            <div className="flex flex-row justify-between sm:flex-col sm:space-y-1">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-gray-100 p-2 dark:bg-gray-700">
                  <Calendar className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                </div>
                <div className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  שינוי מחיר אחרון
                </div>
              </div>
              <div className="text-end sm:text-start">
                <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {formatHebrew(current.date)}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-300">
                  לפני {distance(current.date)}
                </div>
              </div>
            </div>
          </div>

          {previous && (
            <div className="stat-card rounded-xl border border-gray-200 bg-gray-50 p-2 transition-all duration-300 hover:-translate-y-px hover:bg-gray-100 hover:shadow-md hover:shadow-gray-500/10 md:p-3 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700">
              <div className="flex justify-between sm:flex-col sm:space-y-1">
                <div className="flex items-center gap-3">
                  <div className="rounded-lg bg-gray-100 p-2 dark:bg-gray-700">
                    {diff > 0 ? (
                      <TrendingUp className="h-4 w-4 text-red-600 dark:text-red-400" />
                    ) : diff < 0 ? (
                      <TrendingDown className="h-4 w-4 text-green-600 dark:text-green-400" />
                    ) : (
                      <Target className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                    )}
                  </div>
                  <div className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    מחיר קודם
                  </div>
                </div>
                <div className="text-end sm:text-start">
                  <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {previous.price}₪
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-300">
                    {Math.abs(diff)}₪{signChar} ({Math.abs(percent)}%{signChar})
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="stat-card rounded-xl border border-red-200 bg-red-50 p-2 transition-all duration-300 hover:-translate-y-px hover:bg-red-100 hover:shadow-md hover:shadow-red-500/10 md:p-3 dark:border-red-800 dark:bg-red-950/20 dark:hover:bg-red-950/30">
            <div className="flex flex-row justify-between sm:flex-col sm:space-y-1">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-red-100 p-2 dark:bg-red-900/50">
                  <TrendingUp className="h-4 w-4 text-red-600 dark:text-red-400" />
                </div>
                <div className="text-xs font-medium uppercase tracking-wide text-red-600 dark:text-red-400">
                  המחיר הגבוה ביותר
                </div>
              </div>
              <div className="text-end sm:text-start">
                <div className="text-lg font-semibold text-red-900 dark:text-red-100">
                  {max.price}₪
                </div>
                <div className="text-sm text-red-700 dark:text-red-300">
                  לפני {distance(max.date)}
                </div>
              </div>
            </div>
          </div>

          <div className="stat-card rounded-xl border border-green-200 bg-green-50 p-2 transition-all duration-300 hover:-translate-y-px hover:bg-green-100 hover:shadow-md hover:shadow-green-500/10 md:p-3 dark:border-green-800 dark:bg-green-950/20 dark:hover:bg-green-950/30">
            <div className="flex flex-row justify-between sm:flex-col sm:space-y-1">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-green-100 p-2 dark:bg-green-900/50">
                  <TrendingDown className="h-4 w-4 text-green-600 dark:text-green-400" />
                </div>
                <div className="text-xs font-medium uppercase tracking-wide text-green-600 dark:text-green-400">
                  המחיר הנמוך ביותר
                </div>
              </div>
              <div className="text-end sm:text-start">
                <div className="text-lg font-semibold text-green-900 dark:text-green-100">
                  {min.price}₪
                </div>
                <div className="text-sm text-green-700 dark:text-green-300">
                  לפני {distance(min.date)}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className={`grid gap-4 ${inStockOnlyPrices.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
        {inStockOnlyPrices.length > 1 && (
          <div className="stat-card rounded-xl border border-purple-200 bg-purple-50 p-2 transition-all duration-300 hover:-translate-y-px hover:bg-purple-100 hover:shadow-md hover:shadow-purple-500/10 md:p-3 dark:border-purple-800 dark:bg-purple-950/20 dark:hover:bg-purple-950/30">
            <div className="flex flex-row justify-between sm:flex-col sm:space-y-1">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-purple-100 p-2 dark:bg-purple-900/50">
                  <Target className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="text-xs font-medium uppercase tracking-wide text-purple-600 dark:text-purple-400">
                  המחיר הממוצע
                </div>
              </div>
              <div className="text-end sm:text-start">
                <div className="text-lg font-semibold text-purple-900 dark:text-purple-100">
                  {meanPrice}₪
                </div>
                <div className="text-sm text-purple-700 dark:text-purple-300">ממוצע משוקלל</div>
              </div>
            </div>
          </div>
        )}

        <div
          className={`stat-card rounded-xl border border-amber-200 bg-amber-50 transition-all duration-300 hover:-translate-y-px hover:bg-amber-100 hover:shadow-md hover:shadow-amber-500/10 dark:border-amber-800 dark:bg-amber-950/20 dark:hover:bg-amber-950/30 ${hadMoreThanSinglePrice ? 'p-2 md:p-3' : 'p-4'}`}
        >
          <div
            className={
              hadMoreThanSinglePrice
                ? 'flex flex-row justify-between sm:flex-col sm:space-y-1'
                : 'flex items-center justify-between'
            }
          >
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-amber-100 p-2 dark:bg-amber-900/50">
                <Clock
                  className={`text-amber-600 dark:text-amber-400 ${hadMoreThanSinglePrice ? 'h-4 w-4' : 'h-5 w-5'}`}
                />
              </div>
              <div
                className={
                  hadMoreThanSinglePrice
                    ? 'text-xs font-medium uppercase tracking-wide text-amber-600 dark:text-amber-400'
                    : 'text-sm font-medium text-amber-700 dark:text-amber-300'
                }
              >
                נכנס למלאי לראשונה
              </div>
            </div>
            <div className={hadMoreThanSinglePrice ? 'text-end sm:text-start' : 'text-end'}>
              <div className="text-lg font-semibold text-amber-900 dark:text-amber-100">
                {formatHebrew(startDate)}
              </div>
              <div className="text-sm text-amber-700 dark:text-amber-300">
                לפני {distance(startDate)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
